/**
 * The template gallery — FR-I10, FR-I16, T-4.12, T-4.56,
 * [dukaan §1](../../../../../docs/legacy/dukaan.md)'s first step: **pick**.
 *
 * **Nothing opens on load** (FR-I16, C11). Until Wave 12 the first card opened
 * its live demo inline the moment the page was read, so a firm arriving to
 * *choose* was shown one template full-size and seven as buttons. Now every
 * card is equal: a still thumbnail of the template's home page, its name, its
 * profession fit, and two actions — **Preview**, which opens the viewer in a
 * new tab (owner, 2026-09-25: *"it should be in new tab with mobile, and
 * desktop view"*), and **Use this template**.
 *
 * The thumbnail is the real renderer on real sample content, embedded from
 * 3D's `/_demo/<code>` — same origin, so an iframe just works and there is no
 * CORS, no token and no second host involved. **A demo drawn by different code
 * stops being true**, which is why the gallery does not draw its own preview.
 *
 * Two things this screen has to say out loud:
 *
 * 1. **Switching keeps every block** (ADR-0013 §3). Templates are presentation
 *    and blocks are content, so nothing a firm has written is lost — and the
 *    sentence is only reassuring if it is true, so `switchSummary` counts
 *    the actual blocks before it says so.
 * 2. **The demo firm is invented.** The sample files carry that sentence in
 *    their own copy for exactly this reason, so the gallery reads it from
 *    `GET /api/v1/site/samples/{profession}` rather than inventing a second
 *    disclaimer that might disagree with the demo's own.
 *
 * The gallery is **ordered, never filtered** — the service sorts the firm's
 * profession first, then the neutral ones, then the rest. A cost accountant who
 * prefers the advocate's template is not making a mistake.
 *
 * **No filter** (C24 item 6, 2026-10-01). C21 put a row of family chips above
 * the grid; the owner took them off — *"this filter not required user will
 * select as he want from full list"*. So the gallery is the full list in the
 * service's own order, and `FamilyChips`, `familyChips`, `byFamily` and
 * `ALL_FAMILIES` are gone with it.
 *
 * **The families themselves are untouched.** `Template.family` is still what a
 * template *is* and the vocabulary still serves `families` (ADR-0048 §4) —
 * this was presentation and nothing else. `FAMILY_LABELS` and `familyLabel`
 * stay for the vocabulary test and for whatever next needs a family's word.
 *
 * **Seven first** (C24 item 8). Cipher, Campus, Encore, Haven, Keystone,
 * Modern and Voyage lead the grid, then the firm's profession, then the rest.
 * **The order is the service's** (`app/domain/site.py: GALLERY_LEAD`), as it
 * has always been, so the two halves cannot disagree about it.
 *
 * **One line** (C24 item 5). The two paragraphs under the heading are one
 * line: the count and what a switch keeps. Both are **counted, never
 * written** (FR-I16) — the header said *"Six to choose from"* for two waves
 * after there were eight, because it was a number in a sentence.
 */

import { Alert, Badge, Button, Card, Skeleton, buttonClass } from '@fiveox/ui'
import { Check, ExternalLink, Eye } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'

import { TemplateThumb } from '../../components/TemplateThumb'
import { SITE, useEvaluation } from '../../lib/access'
import { ApiError } from '../../lib/api'
import { site, type Sample, type Template } from '../../lib/api/site'
import { useSite } from './useSite'

// -- words --------------------------------------------------------------------

/** FR-I15's profession names — the plural a firm's website would use. */
const PROFESSION_NAMES: Record<string, string> = {
  ca: 'Chartered Accountants',
  cs: 'Company Secretaries',
  cma: 'Cost Accountants',
  advocate: 'Advocates',
}

/** What a template was drawn for, in a phrase a card can carry. */
export function professionFit(profession: string | null): string {
  if (!profession) return 'Suits any practice'
  return `Drawn for ${PROFESSION_NAMES[profession] ?? profession}`
}

/**
 * Whether a template is the one to suggest to this firm — the ones drawn for
 * its profession; for `other` (w13b, ADR-0031 §4), which no template is drawn
 * for, the ones that suit any practice. Every template stays choosable.
 */
export function suggestedFor(templateProfession: string | null, firmProfession: string | null): boolean {
  if (!firmProfession) return false
  if (firmProfession === 'other') return templateProfession === null
  return templateProfession === firmProfession
}

// -- families (C21, ADR-0043 §7) ----------------------------------------------

/**
 * The chips' words, **in ADR-0043 §7's order** — which is the vocabulary's
 * `families` and `site/templates: FAMILIES` (a test holds the three to one
 * order). Not imported from `site/templates`: that module carries every
 * theme's stylesheet, and the staff bundle loads it on demand only.
 */
export const FAMILY_LABELS: Record<string, string> = {
  professional: 'Professional',
  business: 'Business',
  creative: 'Creative',
  care: 'Care & wellness',
  learning: 'Learning',
  causes: 'Causes',
  'food-retail': 'Food & retail',
  travel: 'Travel',
  events: 'Events',
  tech: 'Tech',
}

/** A family's chip word; a key this bundle has no word for is shown as itself. */
export function familyLabel(family: string): string {
  return FAMILY_LABELS[family] ?? capital(family.replace(/-/g, ' '))
}

const NUMBER_WORDS = [
  'no',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
]
const say = (n: number) => NUMBER_WORDS[n] ?? String(n)
const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

/**
 * The gallery's count, from the list itself — never a number in a sentence.
 *
 * **One short clause since C24 item 5** (*"need small and informative
 * discription. single line each"*). It used to break the total down into the
 * profession-drawn and the neutral, which was informative and was most of the
 * line; the breakdown is on each card already (`professionFit`).
 *
 * `23 → "23 designs to choose from."` The words stop at twelve, so thirteen
 * and up are figures (C21).
 */
export function galleryCountLine(templates: Pick<Template, 'profession'>[]): string {
  const total = templates.length
  if (total === 0) return 'No templates to choose from yet.'
  return `${capital(say(total))} design${total === 1 ? '' : 's'} to choose from.`
}

/**
 * What a switch keeps, **counted** — and said *before* the firm presses, not
 * after, because `use()` applies a template with no confirmation step.
 *
 * **Shortened by C24 item 5** to the half of the sentence that is load-bearing:
 * the number of blocks that survive. The part that explained what a template is
 * went; the cards say it by being pictures of pages.
 */
export function switchSummary(blockCount: number): string {
  if (blockCount === 0) return 'Switching changes the look only — nothing you write is ever lost.'
  const blocks = blockCount === 1 ? '1 block' : `${blockCount} blocks`
  return `Switching keeps your ${blocks} exactly as written and changes the look only.`
}

/** The viewer's address (I-7). The firm's profession rides along so the demo
 *  shows the firm's kind of work. */
export function previewHref(code: string, profession: string | null): string {
  const query = profession ? `?profession=${encodeURIComponent(profession)}` : ''
  return `/site/templates/${encodeURIComponent(code)}/preview${query}`
}

// -- the screen ---------------------------------------------------------------

export function SiteTemplates() {
  const { can } = useEvaluation()
  const editable = can(SITE, 'can_update')
  const { site: current, refresh, setSite } = useSite()
  const location = useLocation()
  const navigate = useNavigate()

  const [templates, setTemplates] = useState<Template[] | null>(null)
  const [profession, setProfession] = useState<string | null>(null)
  const [sample, setSample] = useState<Sample | null>(null)
  const [blockCount, setBlockCount] = useState(0)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [applied, setApplied] = useState<string | null>(
    (location.state as { applied?: string } | null)?.applied ?? null,
  )

  const load = useCallback(async () => {
    const body = await site.templates()
    setTemplates(body.data.templates)
    setProfession(body.data.profession)

    // How much a switch would keep. Counted, not asserted.
    const pages = await site.pages('draft')
    const withBlocks = await Promise.all(pages.data.map((page) => site.page(page.id)))
    setBlockCount(withBlocks.reduce((total, p) => total + (p.data.blocks?.length ?? 0), 0))

    if (body.data.profession) {
      // The sample firm's own disclosure line. A failure here costs the
      // sentence and nothing else, so it never blocks the gallery.
      try {
        const s = await site.sample(body.data.profession)
        setSample(s.data)
      } catch {
        setSample(null)
      }
    }
  }, [])

  useEffect(() => {
    void load().catch(() => setError('The templates could not be loaded.'))
  }, [load])

  // Arrived from the viewer's *Use this template*: say so once, then drop the
  // state so a reload does not say it again.
  useEffect(() => {
    if ((location.state as { applied?: string } | null)?.applied) {
      void refresh().catch(() => undefined)
      navigate(location.pathname, { replace: true, state: null })
    }
    // Once, on arrival.
  }, [])

  // The viewer is another tab. When the firm comes back to this one after
  // choosing a template there, *In use* should already have moved.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh().catch(() => undefined)
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [refresh])

  const use = async (template: Template) => {
    setBusy(template.code)
    setError(null)
    setApplied(null)
    try {
      const body = await site.update({ template: template.code })
      setSite(body.data)
      await refresh()
      setApplied(template.code)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That template could not be applied.')
    } finally {
      setBusy(null)
    }
  }

  const appliedTemplate = templates?.find((t) => t.code === applied) ?? null
  // C24 item 6: the full list, in the service's order. No chip, no filter.
  const shown = templates

  return (
    <div className="space-y-6">
      <header>
        <Link to="/site" className="text-sm text-muted-foreground hover:text-foreground">
          ← Your website
        </Link>
        <h1 className="mt-1 text-2xl font-semibold text-foreground">Choose a template</h1>
        {/* C24 item 5: one line, and both numbers in it are counted. */}
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
          {templates
            ? `${galleryCountLine(templates)} ${switchSummary(blockCount)}`
            : 'Loading the templates…'}
        </p>
      </header>

      {appliedTemplate && (
        <Alert tone="success">
          <span className="font-medium">{appliedTemplate.name} is now your website&rsquo;s template.</span>{' '}
          Everything you wrote is where it was; the colours and font are {appliedTemplate.name}&rsquo;s
          now, and you can tune them on{' '}
          <Link to="/site/theme" className="font-medium underline">
            Theme
          </Link>
          .
        </Alert>
      )}

      {error && <Alert tone="error">{error}</Alert>}

      {!editable && (
        <Alert tone="info">
          You can look at every template here. Changing the firm&rsquo;s website is a partner&rsquo;s
          or a practice manager&rsquo;s to do.
        </Alert>
      )}

      <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3" data-gallery="">
        {shown === null
          ? Array.from({ length: 6 }, (_, i) => <CardSkeleton key={i} />)
          : shown.map((template) => {
              const isCurrent = template.code === current?.template
              const href = previewHref(template.code, profession)
              return (
                <Card
                  key={template.code}
                  data-template={template.code}
                  className={[
                    'group flex flex-col overflow-hidden transition-shadow hover:shadow-lg',
                    isCurrent ? 'ring-2 ring-primary' : '',
                  ].join(' ')}
                >
                  {/* The thumbnail is a second door to the same preview — for
                      the pointer only; the named *Preview* link below is the
                      one keyboard and screen-reader users are given. */}
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener"
                    tabIndex={-1}
                    aria-hidden
                    className="relative block border-b border-border"
                  >
                    <TemplateThumb
                      code={template.code}
                      profession={profession}
                      name={template.name}
                    />
                    <span className="absolute inset-0 flex items-center justify-center bg-foreground/0 opacity-0 transition group-hover:bg-foreground/30 group-hover:opacity-100">
                      <span className={buttonClass({ variant: 'outline', size: 'sm' })}>
                        <Eye size={14} /> Preview
                      </span>
                    </span>
                  </a>

                  <div className="flex flex-1 flex-col gap-3 p-4">
                    <div className="flex items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h2 className="text-base font-semibold text-foreground">{template.name}</h2>
                          {isCurrent && (
                            <Badge tone="success" size="sm">
                              <Check size={11} /> In use
                            </Badge>
                          )}
                          {suggestedFor(template.profession, profession) && !isCurrent && (
                            <Badge size="sm">
                              {profession === 'other' ? 'Suits your practice' : 'For your profession'}
                            </Badge>
                          )}
                        </div>
                        <p className="mt-0.5 text-xs font-medium text-muted-foreground">
                          {professionFit(template.profession)}
                        </p>
                      </div>
                      {/* The two colours, so a firm can tell them apart at a
                          glance. Inline styles because they are data, not
                          design tokens. */}
                      <div className="flex shrink-0 -space-x-1.5 pt-0.5">
                        <span
                          className="h-5 w-5 rounded-full border-2 border-background"
                          style={{ background: template.theme.primary }}
                          title={`Primary ${template.theme.primary}`}
                        />
                        <span
                          className="h-5 w-5 rounded-full border-2 border-background"
                          style={{ background: template.theme.accent }}
                          title={`Accent ${template.theme.accent}`}
                        />
                      </div>
                    </div>

                    {/* The trait line — dukaan §1's "Minimal, Clean". The
                        service's own words, so the gallery and the service
                        cannot come to describe a template two ways. */}
                    <p className="text-sm text-muted-foreground">{template.trait}</p>

                    <div className="mt-auto flex flex-wrap items-center gap-2 pt-1">
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener"
                        data-action="preview"
                        className={buttonClass({ variant: 'outline', size: 'sm' })}
                      >
                        Preview <ExternalLink size={14} />
                        <span className="sr-only"> {template.name} (opens in a new tab)</span>
                      </a>
                      {editable && !isCurrent && (
                        <Button
                          size="sm"
                          disabled={busy !== null && busy !== template.code}
                          loading={busy === template.code}
                          loadingText="Applying…"
                          onClick={() => void use(template)}
                        >
                          Use this template
                        </Button>
                      )}
                      {isCurrent && (
                        <span className="text-xs text-muted-foreground">
                          Your website wears this one.
                        </span>
                      )}
                    </div>
                  </div>
                </Card>
              )
            })}
      </div>

      {/* The sample firm's own words about being invented. Read from the
          service rather than written here, so the gallery and the demo cannot
          disagree about what the demo is. */}
      <p className="text-xs leading-relaxed text-muted-foreground">
        {sample?.note ??
          'The demos show invented sample content, not a real practice. Nothing in them is advice.'}
      </p>
    </div>
  )
}

function CardSkeleton() {
  return (
    <Card className="overflow-hidden" aria-hidden>
      <Skeleton className="aspect-[16/10] w-full rounded-none" />
      <div className="space-y-3 p-4">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-3 w-1/2" />
        <Skeleton className="h-3 w-full" />
        <div className="flex gap-2 pt-1">
          <Skeleton className="h-9 w-24" />
          <Skeleton className="h-9 w-36" />
        </div>
      </div>
    </Card>
  )
}
